import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';

import api, { cooperplaceApi } from 'services/api';

import { validatePermission } from 'actions';
import Details from '../Details';
import Register from '../Register';

import { ClientProvider } from './context';

const SETTINGS_SLUGS = ['OBSDEFAULT', 'PRIORITY_FLEET_LOAD_DURATION'];

function getCurrentUserCompanyId() {
  try {
    return JSON.parse(localStorage.getItem('usuario') || '{}')?.company_id;
  } catch (error) {
    return null;
  }
}

function getInitialClientState() {
  return {
    personType: { name: 'Jurídica' },
    is_international: false,
    is_rewards_member: false,
    cgccpf: '',
    ie: '',
    social_number: '',
    social_name: '',
    fantasy_name: '',
    phone: '',
    email: '',
    takerRole: null,
    clientTags: [],
    credit_limit: null,
    tags: [],
    addresses: [],
    settings: [
      {
        type: 'OBSDEFAULT',
        value: '',
      },
      {
        type: 'PRIORITY_FLEET_LOAD_DURATION',
        value: '',
      },
      {
        type: 'REQUIRED_CHECKLIST',
        value: '0',
      },
    ],
    client: {
      sending_tracking: false,
      emails_tracking: null,
      enable_logic_panel: true,
      total_send_email: null,
      time_interval: [],
      cost_centers: [],
    },
    priorityFleetVehicles: [],
    occurrenceTypes: [],
    occurrenceClientTypes: [],
    orderRequired: false,
  };
}

export default function ClientShared() {
  const snackbar = useSnackbar();
  const dispatch = useDispatch();

  const hasPermissionToEdit = validatePermission('EDITAR_CLIENTES');

  const [client, setClient] = useState(getInitialClientState);
  const [isFetching, setFetching] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [errors, setErrors] = useState({});
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [prevClient, setPrevClient] = useState({});
  const [trackingEmailsList, setTrackingEmailsList] = useState([]);
  const [occurrenceTypesClient, setOccurrenceTypesClient] = useState([]);

  const sendIntervalOptions = [
    { id: 1, name: '1 vez ao dia' },
    { id: 2, name: '2 vezes ao dia' },
    { id: 3, name: '3 vezes ao dia' },
    { id: 4, name: '4 vezes ao dia' },
  ];

  const params = useParams();

  function showSnackbar(text, options) {
    snackbar.show(<Text> {text} </Text>, options);
  }

  function setData(data) {
    setClient((prev) => ({ ...prev, ...data }));
  }

  useEffect(() => {
    let isActive = true;

    async function fetchClient() {
      try {
        setFetching(true);
        const response = await api.get(`v3/client/${params.id}`);

        if (!isActive) return;

        const companyId =
          response.data.company_id ??
          response.data.companyId ??
          getCurrentUserCompanyId();
        const settings = (response.data.settings || []).map((setting) => ({
          ...setting,
          person_id: setting.person_id ?? response.data.id,
          company_id: setting.company_id ?? setting.companyId ?? companyId,
        }));

        SETTINGS_SLUGS.forEach((slug) => {
          if (!settings.find((item) => item.type === slug)) {
            settings.push({
              person_id: response.data.id,
              type: slug,
              value: '',
              company_id: companyId,
            });
          }
        });
        setData({
          ...response.data,
          orderRequired: response?.data?.client?.occurrence_order_required ?? false,
          occurrenceClientTypes: response?.data?.occurrenceClientTypes?.map(occurrenceClientType => ({
            id: occurrenceClientType?.occurrenceType?.id,
            name: occurrenceClientType?.occurrenceType?.name,
            slug: occurrenceClientType?.occurrenceType?.slug,
            createdAt: occurrenceClientType?.occurrenceType?.createdAt,
            updatedAt: occurrenceClientType?.occurrenceType?.updatedAt,
            app_visible: occurrenceClientType?.occurrenceType?.app_visible,
            occurrenceFieldsConfigs: occurrenceClientType?.occurrenceClientTypesFieldsConfig?.map(
              item => ({
                id: item?.occurrenceFieldConfig?.id,
                name: item?.occurrenceFieldConfig?.name,
                format: item?.occurrenceFieldConfig?.format,
                type: item?.occurrenceFieldConfig?.type,
                createdAt: item?.occurrenceFieldConfig?.createdAt,
                updatedAt: item?.occurrenceFieldConfig?.updatedAt,
                statusId: item?.statusId,
                status: {
                  id: item?.occurrenceFieldConfigStatus?.id,
                  name: item?.occurrenceFieldConfigStatus?.name,
                  slug: item?.occurrenceFieldConfigStatus?.slug,
                  createdAt: item?.occurrenceFieldConfigStatus?.createdAt,
                  updatedAt: item?.occurrenceFieldConfigStatus?.updatedAt,
                },
              })
            ) || [],
            color: occurrenceClientType?.color,
          })) || [],
          client: {
            ...response?.data?.client,
            time_interval: response?.data?.client?.trackingEmailInterval?.map(item => moment.utc(item.interval_time).format('HH:mm')) || [],
            enable_logic_panel: response?.data?.client?.enable_logic_panel ?? true,
            sending_tracking: response?.data?.client?.sending_tracking ?? false,
            total_send_email: sendIntervalOptions.find(op => op?.id === response?.data?.client?.trackingEmailInterval?.length)
          },
          settings,
        });

        setPrevClient((prev) => ({
          ...prev,
          ...response.data,
          orderRequired: response?.data?.client?.occurrence_order_required ?? false,
          occurrenceClientTypes: response?.data?.occurrenceClientTypes?.map(occurrenceClientType => ({
            id: occurrenceClientType?.occurrenceType?.id,
            name: occurrenceClientType?.occurrenceType?.name,
            slug: occurrenceClientType?.occurrenceType?.slug,
            createdAt: occurrenceClientType?.occurrenceType?.createdAt,
            updatedAt: occurrenceClientType?.occurrenceType?.updatedAt,
            app_visible: occurrenceClientType?.occurrenceType?.app_visible,
            occurrenceFieldsConfigs: occurrenceClientType?.occurrenceClientTypesFieldsConfig?.map(
              item => ({
                id: item?.occurrenceFieldConfig?.id,
                name: item?.occurrenceFieldConfig?.name,
                format: item?.occurrenceFieldConfig?.format,
                type: item?.occurrenceFieldConfig?.type,
                createdAt: item?.occurrenceFieldConfig?.createdAt,
                updatedAt: item?.occurrenceFieldConfig?.updatedAt,
                statusId: item?.statusId,
                status: {
                  id: item?.occurrenceFieldConfigStatus?.id,
                  name: item?.occurrenceFieldConfigStatus?.name,
                  slug: item?.occurrenceFieldConfigStatus?.slug,
                  createdAt: item?.occurrenceFieldConfigStatus?.createdAt,
                  updatedAt: item?.occurrenceFieldConfigStatus?.updatedAt,
                },
              })
            ) || [],
            color: occurrenceClientType?.color,
          })) || [],
          client: {
            ...response?.data?.client,
            time_interval: response?.data?.client?.trackingEmailInterval?.map(item => moment.utc(item.interval_time).format('HH:mm')) || [],
            enable_logic_panel: response?.data?.client?.enable_logic_panel ?? true,
            sending_tracking: response?.data?.client?.sending_tracking ?? false,
            total_send_email: sendIntervalOptions.find(op => op?.id === response?.data?.client?.trackingEmailInterval?.length)
          },
          settings,
        }));


      } catch (ex) {
        if (!isActive) return;
        snackbar.show(<Text>Não foi possível carregar esse cliente</Text>, {
          type: 'error',
        });
      } finally {
        if (isActive) {
          setFetching(false);
        }
      }
    }

    if (params.id) {
      fetchClient();
    }

    return () => {
      isActive = false;
    };
  }, [params.id]);

  useEffect(() => {
    if (!params.id) {
      setClient(getInitialClientState());
      setErrors({});
      setShowRequiredErrors(false);
      setPrevClient({});
      setTrackingEmailsList([]);
      setOccurrenceTypesClient([]);
      setIsEditing(false);
      setFetching(false);
    }
  }, [params.id]);

  useEffect(() => {
    if (params.id) {
      dispatch({
        type: 'VIEW_INSTANCE',
        instance: client.label,
      });
    }
  }, [client]);


  const handleSaveClientOccurrenceTypes = async (data) => {
    const orderRequired = data.orderRequired ?? client.data?.orderRequired ?? false;

    const payloadOccurrenceTypes = (data.occurrenceClientTypes || []).map((type) => ({
      id: type.id,
      name: type.name,
      slug: type.slug,
      app_visible: type.app_visible,
      color: type.color,
      order: type.order,
      occurrenceFieldsConfigs: (type.occurrenceFieldsConfigs || []).map((field) => ({
        id: field.id,
        name: field.name,
        format: field.format ?? null,
        type: field.type,
        status: {
          id: field?.status?.id ?? field?.statusId ?? 0,
          name: field?.status?.name ?? 'Opcional',
          slug: field?.status?.slug ?? 'OPTIONAL',
        },
      })),
    }));

    try {
      await cooperplaceApi.put(`occurrences/client/${params.id}`, {
        occurrencesTypes: payloadOccurrenceTypes,
        orderRequired
      });

      snackbar.show(<Text>Alterações salvas com sucesso</Text>, { type: 'success' });

    } catch (error) {
      client.setData(prev => ({
        ...prev,
        occurrenceClientTypes: []
      }));

      setIsEditing(false);
      snackbar.show(<Text>Não foi possível salvar as alterações</Text>, {
        type: 'error',
      });
    }
  };

  return (
    <ClientProvider
      value={{
        data: client,
        setData,
        setClient,
        isFetching,
        setFetching,
        errors,
        setErrors,
        showRequiredErrors,
        setShowRequiredErrors,
        showSnackbar,
        prevClient,
        setPrevClient,
        trackingEmailsList,
        setTrackingEmailsList,
        sendIntervalOptions,
        canEditData: hasPermissionToEdit,
        setOccurrenceTypesClient,
        occurrenceTypesClient,
        handleSaveClientOccurrenceTypes,
        setIsEditing,
        isEditing,
      }}
    >
      {params.id ? <Details /> : <Register />}
    </ClientProvider>
  );
}
